import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, FileText, Sparkles, Trash2, Eye, Edit, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export default function PagesList() {
  const [pages, setPages] = useState([]);
  const [products, setProducts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/pages').then(r => setPages(r.data)).catch(() => {});
    api.get('/products').then(r => {
      const map = {};
      r.data.forEach(p => { map[p.id] = p; });
      setProducts(map);
    }).catch(() => {});
  }, []);

  const deletePage = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await api.delete(`/pages/${id}`);
      setPages(prev => prev.filter(p => p.id !== id));
      toast.success('Page deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="pages-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit']">Pages</h1>
          <p className="text-muted-foreground mt-1">{pages.length} pages</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pages.map(page => {
          const prod = products[page.product_id];
          return (
            <Card key={page.id} className="group hover:border-[#1A9DA5]/50 hover:shadow-md transition-all" data-testid={`page-card-${page.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <Badge variant="outline" className="text-xs">{page.page_type === 'product_page' ? 'Product' : 'Landing'}</Badge>
                    <Badge variant="outline" className="text-xs">{page.template_type}</Badge>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical className="w-3 h-3" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/pages/${page.id}`)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => window.open(`/l/${page.slug}`, '_blank')}><Eye className="w-4 h-4 mr-2" />Preview</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => deletePage(page.id)}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="font-semibold text-sm mb-1 line-clamp-2 font-['Outfit'] group-hover:text-[#1A9DA5] transition-colors cursor-pointer" onClick={() => navigate(`/admin/pages/${page.id}`)}>
                  {page.title || 'Untitled Page'}
                </h3>
                {prod && <p className="text-xs text-muted-foreground">Product: {prod.title}</p>}
                <p className="text-xs text-muted-foreground mt-1">/{page.slug}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
